import {Component, inject, OnInit} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatFormField, MatHint, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {MatOption, MatSelect} from '@angular/material/select';
import {AsyncPipe} from '@angular/common';
import {ReactiveFormsModule} from '@angular/forms';
import {SharedModule} from '../../../../shared/shared.module';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {AdxErpPanelFilterService} from '../../service/adx-erp-panel-filter.service';
import {AdxErpLookupService} from '../../service/adx-erp-lookup.service';
import {TramsDataSourceService} from 'src/app/payments/service/trams-data-source.service';
import {TramsDataSourceModel} from 'src/app/payments/models/trams-data-source';
import {AdxErpPanelFilterModel} from '../../models/adx-erp-panel-filter-model';
import {Client} from 'src/app/client/service/client.model';

@Component({
  selector: 'app-adx-erp-filter-modal',
  imports: [
    AsyncPipe,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatHint,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatOption,
    MatRadioButton,
    MatRadioGroup,
    MatSelect,
    MatSuffix,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './adx-erp-filter-modal.component.html',
  styles: [`
    .filter__section + .filter__section {
      margin-top: 18px;
    }
    .filter__section-title {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: rgba(0, 0, 0, 0.54);
      padding-bottom: 6px;
      margin-bottom: 10px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }
    .filter__fields {
      display: flex;
      flex-direction: column;
      gap: 4px;
      width: 100%;
      box-sizing: border-box;
    }
    .filter__fields.two-col {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 8px 20px;
    }
    mat-form-field, .autocomplete-field {
      width: 100%;
      min-width: 0;
    }
    .autocomplete-field {
      display: flex;
      flex-direction: column;
    }
    .autocomplete-label {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.6);
      margin-bottom: 4px;
    }
    .radio-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .radio-label {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.6);
    }
    .radio-group {
      display: flex;
      gap: 16px;
    }
  `],
})
export class AdxErpFilterModalComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<AdxErpFilterModalComponent>);
  private readonly filterService = inject(AdxErpPanelFilterService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly lookupService = inject(AdxErpLookupService);
  private readonly tramsDataSourceService = inject(TramsDataSourceService);

  protected readonly form = this.filterService.form;

  protected readonly travelTypes$ = this.lookupService.getTravelTypes();

  protected readonly externalTravelServiceTypes$ = this.lookupService.getExternalTravelServiceTypes();

  protected readonly dataSources$ = this.tramsDataSourceService.getTramsDataSourceList();

  protected isSmall = false;

  constructor() {
    this.breakpointObserver.observe([
      Breakpoints.Medium,
      Breakpoints.Small,
      Breakpoints.XSmall,
    ]).subscribe(result => {
      this.isSmall = result.matches;
    });
  }

  public ngOnInit(): void {
    this.patchForm();
  }

  private patchForm(): void {
    const filters = this.filterService.filtersValue();
    this.form.patchValue({
      AdxInvoiceId: filters.AdxInvoiceId,
      RecordLocator: filters.RecordLocator,
      ErpOrderNumber: filters.ErpOrderNumber,
      ClientName: filters.ClientName,
      CreatedDateFrom: filters.CreatedDateFrom,
      CreatedDateTo: filters.CreatedDateTo,
      ErpSentDateFrom: filters.ErpSentDateFrom,
      ErpSentDateTo: filters.ErpSentDateTo,
      ErpResponseDateFrom: filters.ErpResponseDateFrom,
      ErpResponseDateTo: filters.ErpResponseDateTo,
      IsMor: filters.IsMor,
      TravelTypeId: filters.TravelTypeId,
      ExternalTravelServiceTypeId: filters.ExternalTravelServiceTypeId,
      VendorName: filters.VendorName,
      AgentName: filters.AgentName,
      DataSourceId: filters.DataSourceId,
      InvoiceGroup: filters.InvoiceGroup,
    });
  }

  protected apply(): void {
    const raw = this.form.getRawValue();
    const clientValue = raw.ClientName as unknown as Client | string | null;
    const clientName = clientValue && typeof clientValue === 'object'
      ? (clientValue as Client).profileName ?? null
      : clientValue ?? null;

    const filters: AdxErpPanelFilterModel = {
      AdxInvoiceId: raw.AdxInvoiceId,
      RecordLocator: raw.RecordLocator,
      ErpOrderNumber: raw.ErpOrderNumber,
      ClientName: clientName,
      CreatedDateFrom: raw.CreatedDateFrom,
      CreatedDateTo: raw.CreatedDateTo,
      ErpSentDateFrom: raw.ErpSentDateFrom,
      ErpSentDateTo: raw.ErpSentDateTo,
      ErpResponseDateFrom: raw.ErpResponseDateFrom,
      ErpResponseDateTo: raw.ErpResponseDateTo,
      IsMor: raw.IsMor,
      TravelTypeId: raw.TravelTypeId,
      ExternalTravelServiceTypeId: raw.ExternalTravelServiceTypeId,
      VendorName: raw.VendorName,
      AgentName: raw.AgentName,
      DataSourceId: raw.DataSourceId,
      InvoiceGroup: raw.InvoiceGroup,
      ReloadGrid: true,
    };

    this.filterService.setFilters(filters);
    this.close();
  }

  protected reset(): void {
    this.form.reset();
    this.filterService.setFilters({
      AdxInvoiceId: null,
      RecordLocator: null,
      ErpOrderNumber: null,
      ClientName: null,
      CreatedDateFrom: null,
      CreatedDateTo: null,
      ErpSentDateFrom: null,
      ErpSentDateTo: null,
      ErpResponseDateFrom: null,
      ErpResponseDateTo: null,
      IsMor: null,
      TravelTypeId: null,
      ExternalTravelServiceTypeId: null,
      VendorName: null,
      AgentName: null,
      DataSourceId: null,
      InvoiceGroup: null,
      ReloadGrid: true,
    });
    this.close();
  }

  protected close(): void {
    this.dialogRef.close();
  }

  /**
   * Sources are identified by number day to day, so the id is always shown. Some rows carry a
   * SourceName that is just the id again, and those would otherwise read "102 (102)".
   */
  protected dataSourceLabel(dataSource: TramsDataSourceModel): string {
    const name = dataSource.sourceName?.trim();
    return !name || name === String(dataSource.id) ? `${dataSource.id}` : `${name} (${dataSource.id})`;
  }

  protected clearIsMorFilter(): void {
    this.form.patchValue({IsMor: null});
  }
}
